import React, { useState, useEffect } from "react";
import {
  verifyPasswordResetCode,
  confirmPasswordReset,
  getAuth,
} from "firebase/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = getAuth();

  const oobCode = searchParams.get("oobCode");

  const [verified, setVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!oobCode) {
      setMessage("Missing reset code.");
      setLoading(false);
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then(() => {
        setVerified(true);
        setLoading(false);
      })
      .catch(() => {
        setMessage("Invalid or expired reset code.");
        setLoading(false);
      });
  }, [auth, oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setMessage("✅ Password set! Redirecting to login...");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setMessage("❌ Failed to set password. Try again.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row h-full md:h-screen font-sans text-gray-900">
      {/* Left Panel */}
      <div
        className="md:w-1/2 w-full flex flex-col justify-center items-center p-8 md:p-10 relative overflow-hidden"
        style={{
          backgroundImage: 'url("/vintage-phone-pattern.png")',
          backgroundRepeat: "repeat",
          backgroundPosition: "center",
          backgroundSize: "200px",
          backgroundColor: "#0F0F0F",
        }}
      >
        <div className="absolute top-4 left-4 flex items-center gap-2 md:gap-3 z-10">
          <div className="bg-[#179D68] text-white font-bold text-lg md:text-xl px-2 md:px-3 py-1 rounded-md shadow-md border border-white/20 tracking-wide">
            DR
          </div>
          <div className="text-lg md:text-xl font-semibold tracking-wider text-white drop-shadow-md bg-[#179D68] px-2 py-1 rounded-md">
            Dead Ringers
          </div>
        </div>

        <motion.h1
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-2xl md:text-4xl font-extrabold uppercase text-center z-10 mt-12 md:mt-0 mb-4 drop-shadow-lg bg-[#179D68] px-4 py-2 rounded-md"
          style={{ fontFamily: '"Montserrat", sans-serif' }}
        >
          Set Your Password
        </motion.h1>
      </div>

      {/* Right Panel */}
      <div className="md:w-1/2 w-full flex justify-center items-center bg-[#f4f4f4] p-6 md:p-10">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md p-6 md:p-8 bg-white shadow-2xl rounded-xl border border-gray-300"
        >
          <h2 className="text-xl md:text-2xl font-bold mb-6 text-gray-800 text-center">
            Create New Password
          </h2>

          {loading ? (
            <p className="text-gray-600 text-center">Verifying link...</p>
          ) : verified ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#179D68]"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-[#179D68] hover:bg-[#148c5a] text-white py-3 rounded-lg font-semibold shadow-md transition"
              >
                Set Password
              </button>
            </>
          ) : (
            <p className="text-red-600 font-medium text-center">{message}</p>
          )}

          {message && verified && (
            <p className="text-sm text-gray-700 mt-4 text-center">{message}</p>
          )}
        </form>
      </div>
    </div>
  );
}

export default SetPassword;
